import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { BaseLogo, getLogoOrgId } from "../core/models/entities/BaseLogo";
import { enviroment } from "../enviroment";
import { useApiClient } from "../data/clients/ApiClient";
import { mapToLogo } from "../data/models/mappers/mapLogo";
import {
  logoDetailKey,
  patchLogoRowsFromDetail,
} from "../data/repositories/logoDetailCache";
import {
  isLogoProductPending,
  isLogoUploadPending,
  isProductPendingStale,
  isUploadRejected,
} from "../utils/functions/productPending";
import { productPendingPollDelay } from "../utils/functions/productPendingPoll";
import { isLogoPriceHeld } from "../utils/functions/priceHeld";
import {
  PRICE_HELD_FOCUS_RESTART_DEBOUNCE_MS,
  priceHeldPollDelay,
} from "../utils/functions/priceHeldPoll";

// Flags REACT_APP_PRODUCT_PENDING_ENABLED / REACT_APP_CLONE_INSTANT_ENABLED /
// REACT_APP_LOGO_PRICE_HELD_ENABLED - build-time constant, so the export at
// the bottom is fixed for the lifetime of the bundle and hook order never
// varies at runtime (same idiom as useEditorUploadPendingPoll). Callers only
// ever have a pending or held row under one of the three flags, so with all
// off nothing can be scheduled.
const listPollEnabled =
  enviroment.productPendingEnabled ||
  enviroment.cloneInstantEnabled ||
  enviroment.logoPriceHeldEnabled;

/**
 * Above this many pending rows on the page ONE list refetch (GetAllLogos)
 * per tick replaces the per-row GetLogo calls: the list call IS the batch
 * endpoint (there is no GET logos/pending-state?ids=…), and N GetLogo of
 * ~1.4-1.8 s each cost more than one list call from N = 4 on.
 */
export const LIST_PENDING_POLL_MAX_DETAIL_ROWS = 3;

export interface LogoListPendingPollArgs {
  /** The rows the list shows right now (page.results). */
  rows: readonly BaseLogo[] | undefined;
  /**
   * The list's settled state (success, not fetching, not previous data) -
   * the same gate as useStorageFreshness, so rows kept on screen from the
   * previous query (keepPreviousData) are never polled.
   */
  settled: boolean;
  /** The list's own refetch (useAllLogos().refetch) - used above the cap. */
  refetchList: () => unknown;
  /**
   * The list's QUIET re-read (LogoRepository.refetchLogosQuietly for the
   * screen's current query) - used for a HELD ladder (flag
   * REACT_APP_LOGO_PRICE_HELD_ENABLED). Unlike `refetchList` it never
   * touches the query's status / isFetching: a failed read leaves the rows
   * on screen (the observer's refetch would flip the list to its error
   * view after react-query's retries - «kunden ser aldrig fejl»), and the
   * plan runs from the tick instead of waiting for the round trip (the
   * `settled` gate below stays true). Never rejects.
   */
  refetchListQuietly: () => Promise<unknown>;
  /**
   * Rows another view is ALREADY polling on the same detail key - the row
   * open in the popup (LogoDetailsWrapper's useProductPendingPoll, rendered
   * inside the list screen so both hooks are live at once). Those rows are
   * left to that view: skipped in the tick, never fetched from here. The
   * popup's own landing is copied into the list row by the wrapper's
   * patchLogoRowsFromDetail effect, so the row still clears. Not part of
   * the budget bookkeeping: opening/closing the popup neither restarts the
   * budget nor counts as a new id.
   */
  excludeIds?: readonly number[];
}

/**
 * The rows still worth a re-read: pending under an ENABLED variant and
 * younger than the 10-minute stale line. A stale row already reads "Prices
 * delayed" / "Logo preparation delayed" (worker poisoned, ops mail sent) and
 * is not polled - the badge changes by itself when the sweep lands it and
 * the user opens or reloads the page. A REJECTED upload (flag
 * REACT_APP_UPLOAD_PENDING_ENABLED, uploadFailedReason set) keeps its stamp
 * by design until the user removes the row - nothing will ever land on it,
 * so it is not polled either. A HELD ladder (flag
 * REACT_APP_LOGO_PRICE_HELD_ENABLED: priceHeld with no price rows) counts
 * too - it carries no stamp to age it by (the hold itself ends within
 * seconds to the next 5-minute drain of the API's price queue), so the
 * poll's budget alone bounds its re-read (5 ticks for a page with a held
 * row, priceHeldPollDelay). Pure; `now` injectable for tests.
 */
export function pendingListCandidates(
  rows: readonly BaseLogo[] | undefined,
  now: number = Date.now()
): BaseLogo[] {
  return (rows ?? []).filter((row) => {
    if (!row || typeof row.id !== "number" || row.id <= 0) return false;
    const product =
      isLogoProductPending(row) &&
      !isProductPendingStale(row.productPendingSinceUtc, now);
    const upload =
      isLogoUploadPending(row) &&
      !isUploadRejected(row) &&
      !isProductPendingStale(row.uploadPendingSinceUtc, now);
    return product || upload || isLogoPriceHeld(row);
  });
}

type LogoListPendingPoll = (args: LogoListPendingPollArgs) => void;

/**
 * Bounded re-read for the "Prices on their way" / "Preparing logo…" rows of
 * the logo LIST (flags REACT_APP_PRODUCT_PENDING_ENABLED /
 * REACT_APP_CLONE_INSTANT_ENABLED).
 *
 * Why: every other pending re-read (popup, editor, cart) lands in the DETAIL
 * entry of one logo, and the list entry is only rewritten by a query change
 * or the return-to-list refetch - which races the API worker (the list is
 * refetched before the worker has cleared the stamp) - so a row stayed on
 * "Prices on their way" until F5 (prod 675889, 11/9). This hook is the one
 * re-read that works on the rows themselves.
 *
 * Schedule: the shared 3/8/15 s plan (productPendingPollDelay), at most
 * MAX_PRODUCT_PENDING_POLLS ticks per view (a page with a HELD row runs the
 * longer priceHeldPollDelay plan, 5 ticks - see the budget below); the
 * budget restarts only when a
 * NEW pending id appears on the page (same rule as useProductPendingPoll -
 * an id that lands and leaves keeps the running budget), and the loop stops
 * the moment no candidate is left, the view unmounts or the budget is spent
 * (the badge then simply stays; a new view = a new budget, a query change
 * only when it shows an id not polled before - the same id on another page
 * keeps the running budget). Only armed while the list is `settled`; the
 * candidate set and the budget are still tracked while it is not, so a
 * refetch of the very same rows never hands them a fresh budget.
 *
 * Per tick the candidates are re-evaluated on the CURRENT rows, minus the
 * rows in `excludeIds` (the row open in the popup polls itself - one poller
 * per detail key, so the popup + list never spend 6 GetLogo on one logo);
 * the cap below is judged on what is left:
 *  - N <= LIST_PENDING_POLL_MAX_DETAIL_ROWS: one GetLogo per row through
 *    queryClient.fetchQuery on the row's own detail key (["logos", actAs,
 *    id]) - the REAL detail entry, never a stub, so a popup opened next finds
 *    a cached GetLogo (0 extra calls) - and patchLogoRowsFromDetail copies
 *    the landed state into the list rows (0 calls). A fetch cancelled by a
 *    mutation's cancelInFlightDetailFetch (same key) is swallowed: the
 *    mutation's own seed wins, the next tick judges the new rows.
 *  - N > cap: ONE refetchList (GetAllLogos) instead of N GetLogo.
 *
 *  - a HELD ladder among the candidates (flag
 *    REACT_APP_LOGO_PRICE_HELD_ENABLED; the API's list hold, js-kitmaker-api
 *    #1295): ONE QUIET list read (`refetchListQuietly`) whatever the count.
 *    A held row is NEVER read through GetLogo from here - that synchronous
 *    supplier look-up is what the API's list path defers to its worker -
 *    and ONE GetAllLogos lands every held and pending row of the page at
 *    once (the API fills the serial channel within seconds (prime) or by
 *    its next 5-minute drain, so the next list answer carries the DKK
 *    ladder and priceHeld is gone). Quiet, not the observer's refetch: a
 *    failed background read must not turn the list into the error view,
 *    and the plan must run from the tick rather than wait for the answer.
 *    Pending rows on the same tick ride along in that list read instead of
 *    their per-row GetLogo (fewer calls, same landing); a page without a
 *    held row keeps the per-row path / `refetchList` exactly as above.
 *
 * Call budget per list view with pending rows: <= 3 ticks x min(N, 3)
 * GetLogo (N <= 3) or <= 3 GetAllLogos (N > 3); 0 when no row is pending or
 * every pending row is older than 10 minutes; 0 from here for the row open
 * in the popup (<= 3 from the popup's own poll); typically 1 GetLogo at 3 s
 * (worker p50 < 4 s). With a held row on the page (owner decision 1b/1c,
 * 31/10): 0 calls per row, <= 5 quiet GetAllLogos per view at 3 / 11 / 26 /
 * 60 / 330 s after the page settled - the shared plan plus TWO slow ticks
 * (priceHeldPollDelay: 34 s and 270 s after the previous tick, so one
 * 5-minute drain of the API's price queue is always inside the window;
 * typically 1 read, the prime fills the channel within seconds). The delays
 * run from each tick (the quiet read never drops `settled`, so no round trip
 * is added; the last answer lands at 330 s + one list latency). The slow
 * ticks are armed only while a held row is on the page and read nothing
 * when the hold has ended in the meantime (a page with pending rows only
 * keeps its 3 ticks). Then the badge stays until a NEW held or pending id
 * on the page (the budget restarts exactly as for pending rows), a popup or
 * cart GetLogo patches the row (flag B), the next view, F5 - or a RETURN TO
 * THE TAB (document visibilitychange -> visible, or window focus, while a
 * held row is still on the page): that restarts the budget once (reads at 3
 * / 11 / 26 / 60 / 330 s again - typically 1, the hold is over by then),
 * never twice within 30 s of the running budget's start
 * (PRICE_HELD_FOCUS_RESTART_DEBOUNCE_MS: the two events of one tab switch
 * count once, tab flipping cannot spam, a return during the fast plan
 * changes nothing). Worst case while a held row persists and the user keeps
 * returning: 3 GetAllLogos per 30 s. react-query's own refetchOnWindowFocus
 * is off globally (index.tsx), so this is the list's only re-read on return.
 * The listeners exist only while a held row is on the page (none without).
 * Per night: 0. Flags off (all three): no hook body at all.
 */
function useLogoListPendingPollEnabled({
  rows,
  settled,
  refetchList,
  refetchListQuietly,
  excludeIds,
}: LogoListPendingPollArgs): void {
  const queryClient = useQueryClient();
  const apiClient = useApiClient();
  const attempt = useRef(0);
  const [tick, setTick] = useState(0);

  // The timer fires later: it reads the rows, the excluded ids, the client
  // and the refetch of THAT moment (a patch or a refetch may have replaced
  // what the list held when the timer was armed; the popup may have opened
  // or closed since).
  const latestRef = useRef({
    rows,
    refetchList,
    refetchListQuietly,
    apiClient,
    excludeIds,
  });
  latestRef.current = {
    rows,
    refetchList,
    refetchListQuietly,
    apiClient,
    excludeIds,
  };

  // Tracked on the rows as shown - settled or not - so a refetch of the same
  // rows (settled false -> true) is not a "new" set. Sorted so the order of
  // the page never counts as a change.
  const candidatesNow = pendingListCandidates(rows);
  const idsSignature = candidatesNow
    .map((row) => row.id)
    .sort((a, b) => a - b)
    .join(",");
  // A HELD row on the page (flag logoPriceHeldEnabled; always false with it
  // off): the page runs the longer plan and listens for a return to the tab.
  const heldOnPage = candidatesNow.some((row) => isLogoPriceHeld(row));

  // The budget restarts only when an id we have NOT been polling appears; an
  // id that leaves (landed, or paged away) keeps the running count.
  const polledIds = useRef<Set<number>>(new Set());
  // When the running budget began (mount, a NEW id, a return to the tab):
  // the clock of the 30 s debounce below.
  const budgetStartedAt = useRef(Date.now());
  useEffect(() => {
    const ids = idsSignature === "" ? [] : idsSignature.split(",").map(Number);
    const hasNewId = ids.some((id) => !polledIds.current.has(id));
    polledIds.current = new Set(ids);
    if (hasNewId) {
      attempt.current = 0;
      budgetStartedAt.current = Date.now();
    }
  }, [idsSignature]);

  // Owner decision 1c (31/10): a return to the tab restarts the budget ONCE
  // while a held row is still on the page. The hold usually ends at the
  // API's next 5-minute drain - often while the user is on another tab - and
  // react-query's own refetchOnWindowFocus is off globally (index.tsx), so
  // without this the badge and the lock stood until F5. Both events of one
  // tab switch (document visibilitychange -> visible, then window focus) and
  // any tab flipping count once per 30 s (PRICE_HELD_FOCUS_RESTART_DEBOUNCE_MS,
  // measured from when the running budget began, so a return during the fast
  // plan changes nothing either). A restart = attempt 0: the plan runs again
  // from 3 s (typically 1 read - the hold is over). Judged on the rows of
  // THAT moment; the listeners exist only while a held row is on the page
  // (none without, and none with the flag off = no hook body).
  useEffect(() => {
    if (!heldOnPage) return;
    const onReturn = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - budgetStartedAt.current < PRICE_HELD_FOCUS_RESTART_DEBOUNCE_MS) {
        return;
      }
      const stillHeld = pendingListCandidates(latestRef.current.rows).some(
        (row) => isLogoPriceHeld(row)
      );
      if (!stillHeld) return;
      budgetStartedAt.current = now;
      attempt.current = 0;
      setTick((t) => t + 1);
    };
    document.addEventListener("visibilitychange", onReturn);
    window.addEventListener("focus", onReturn);
    return () => {
      document.removeEventListener("visibilitychange", onReturn);
      window.removeEventListener("focus", onReturn);
    };
  }, [heldOnPage]);

  useEffect(() => {
    if (!settled || idsSignature === "") return;
    const plan = enviroment.productPendingPollMs;
    // A page with a held row runs the longer plan (two slow ticks after the
    // shared three, decision 1b); pending rows alone keep today's 3 ticks.
    const delay = heldOnPage
      ? priceHeldPollDelay(attempt.current, plan)
      : productPendingPollDelay(attempt.current, plan);
    if (delay === undefined) return;
    const timer = setTimeout(() => {
      // Beyond the shared plan = one of the held rows' slow ticks: it exists
      // for a held row only, so when the hold has ended since it was armed it
      // reads nothing for the pending rows (their 3 ticks are spent).
      const slowTick =
        productPendingPollDelay(attempt.current, plan) === undefined;
      attempt.current += 1;
      const current = latestRef.current;
      // The excluded rows (open in the popup) are dropped BEFORE the cap is
      // judged and before any fetch - but not from the budget bookkeeping
      // above, so a popup opened and closed on the same row hands out no
      // fresh budget. A tick left with nothing to read still counts: the
      // schedule is the view's, not the row's.
      const excluded = new Set(current.excludeIds ?? []);
      const candidates = pendingListCandidates(current.rows).filter(
        (row) => !excluded.has(row.id)
      );
      // A held ladder (flag logoPriceHeldEnabled) is re-read through the
      // QUIET list read only - never a GetLogo per held row, never the
      // observer's refetch (see the budget above); always false with the
      // flag off, so the two branches below are exactly today's.
      const anyHeld = candidates.some((row) => isLogoPriceHeld(row));
      if (anyHeld) {
        void Promise.resolve()
          .then(() => current.refetchListQuietly())
          .catch(() => undefined);
      } else if (slowTick) {
        // The hold ended between arming and firing: nothing to read.
      } else if (candidates.length > LIST_PENDING_POLL_MAX_DETAIL_ROWS) {
        void Promise.resolve()
          .then(() => current.refetchList())
          .catch(() => undefined);
      } else {
        candidates.forEach((row) => {
          void queryClient
            .fetchQuery({
              queryKey: logoDetailKey(row.id),
              queryFn: () =>
                current.apiClient
                  .getLogo(getLogoOrgId(row), row.id)
                  .then(mapToLogo),
              staleTime: 0,
            })
            .then((detail) => {
              patchLogoRowsFromDetail(queryClient, detail);
            })
            .catch(() => undefined);
        });
      }
      // Re-armed at once (not after the answer): the schedule stays 3/8/15 s
      // from the tick whatever the round trip, and react-query dedupes a
      // fetch still in flight on the same key into one call.
      setTick((t) => t + 1);
    }, delay);
    return () => clearTimeout(timer);
  }, [settled, idsSignature, heldOnPage, tick, queryClient]);
}

// Flag off: no hook body at all (no query-client read, no api client, no
// timer) - the call site keeps one hook call either way.
function useLogoListPendingPollDisabled(): void {}

export const useLogoListPendingPoll: LogoListPendingPoll = listPollEnabled
  ? useLogoListPendingPollEnabled
  : useLogoListPendingPollDisabled;
